import * as React from "react";
import { getPosthogClient, initPosthog } from "~/lib/posthog-client";
import { initSentryClient } from "~/lib/sentry-client";
import {
  getCookieConsent,
  setCookieConsent,
  clearCookieConsent,
  type CookieConsentCategories,
} from "~/lib/cookie-consent-state";
import * as m from "~/paraglide/messages";

type View = "initial" | "customize";

/**
 * Reset cookie consent — clears stored preferences, opts out of PostHog,
 * closes Sentry, and reloads the page so the consent banner re-appears.
 */
export function resetCookieConsent(): void {
  if (typeof window === "undefined") return;
  clearCookieConsent();
  getPosthogClient()?.opt_out_capturing();
  // Sentry has no clean shutdown API — reload handles it
  window.location.reload();
}

/**
 * GDPR / ePrivacy cookie consent banner with granular per-category toggles.
 *
 * Three-action pattern: Accept All, Reject All, Customize.
 * Customize expands to show toggles for Analytics (PostHog) and Error Tracking (Sentry).
 * Essential cookies are always on (not toggleable). Stripe is informational only.
 *
 * PostHog and Sentry only initialize when their respective category is opted in (D-16).
 */
export function CookieConsent() {
  const [hasConsent, setHasConsent] = React.useState(true); // SSR: hide
  const [view, setView] = React.useState<View>("initial");
  const [visible, setVisible] = React.useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = React.useState(false);
  const [errorTrackingEnabled, setErrorTrackingEnabled] = React.useState(false);
  const bannerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const stored = getCookieConsent();

    if (stored) {
      // Consent already given — conditionally init tracking
      setHasConsent(true);
      if (stored.analytics) {
        initPosthog();
        getPosthogClient()?.opt_in_capturing();
      }
      if (stored.errorTracking) {
        initSentryClient();
      }
      return;
    }

    // No consent yet — show banner
    setHasConsent(false);
    requestAnimationFrame(() => {
      setVisible(true);
      setTimeout(() => bannerRef.current?.focus(), 350);
    });
  }, []);

  // Already chose — render nothing
  if (hasConsent) return null;

  function applyConsent(categories: CookieConsentCategories) {
    setCookieConsent(categories);

    if (categories.analytics) {
      initPosthog();
      getPosthogClient()?.opt_in_capturing();
    } else {
      getPosthogClient()?.opt_out_capturing();
    }

    if (categories.errorTracking) {
      initSentryClient();
    }

    setHasConsent(true);
  }

  function handleAcceptAll() {
    applyConsent({ essential: true, analytics: true, errorTracking: true });
  }

  function handleRejectAll() {
    applyConsent({ essential: true, analytics: false, errorTracking: false });
  }

  function handleSavePreferences() {
    applyConsent({
      essential: true,
      analytics: analyticsEnabled,
      errorTracking: errorTrackingEnabled,
    });
  }

  const reduceMotion =
    typeof window !== "undefined" &&
    (window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
      document.documentElement.getAttribute("data-reduce-motion") === "true");

  return (
    <div
      ref={bannerRef}
      tabIndex={-1}
      className="fixed z-50 outline-none"
      style={{
        /* Floating card centered horizontally, 16px from bottom on all sides */
        left: "1rem",
        right: "1rem",
        bottom: "1rem",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: "56rem",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-xl, 1.25rem)",
        boxShadow: "var(--shadow-lg)",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(24px)",
        transition: reduceMotion
          ? "none"
          : "opacity 350ms var(--ease-out, cubic-bezier(0.16,1,0.3,1)), transform 350ms var(--ease-out, cubic-bezier(0.16,1,0.3,1))",
      }}
      role="alertdialog"
      aria-label={m.landing_cookie_settings()}
    >
      <div className="px-5 py-4 sm:px-6 sm:py-5">
        {view === "initial" ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="flex items-start gap-3 min-w-0">
              <span
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                style={{
                  background: "var(--accent-subtle)",
                  color: "var(--accent-text)",
                }}
              >
                <CookieIcon className="h-4 w-4" />
              </span>
              <p
                className="text-sm leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {m.cookie_consent_message()}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0 sm:flex-nowrap">
              <button
                type="button"
                onClick={handleRejectAll}
                className="btn btn-secondary btn-sm"
              >
                {m.cookie_consent_reject_all()}
              </button>
              <button
                type="button"
                onClick={() => setView("customize")}
                className="btn btn-ghost btn-sm"
              >
                {m.cookie_consent_customize()}
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="btn btn-primary btn-sm"
              >
                {m.cookie_consent_accept_all()}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                style={{
                  background: "var(--accent-subtle)",
                  color: "var(--accent-text)",
                }}
              >
                <CookieIcon className="h-4 w-4" />
              </span>
              <p
                className="text-sm leading-relaxed"
                style={{ color: "var(--text-secondary)" }}
              >
                {m.cookie_consent_message()}
              </p>
            </div>

            {/* Category list — flat rows separated by dividers */}
            <div
              className="divide-y rounded-lg border"
              style={{ borderColor: "var(--border-subtle)" }}
            >
              <CategoryRow
                label={m.cookie_consent_essential()}
                description={m.cookie_consent_essential_desc()}
                checked={true}
                disabled={true}
              />
              <CategoryRow
                label={m.cookie_consent_analytics()}
                description={m.cookie_consent_analytics_desc()}
                checked={analyticsEnabled}
                onChange={setAnalyticsEnabled}
              />
              <CategoryRow
                label={m.cookie_consent_error_tracking()}
                description={m.cookie_consent_error_tracking_desc()}
                checked={errorTrackingEnabled}
                onChange={setErrorTrackingEnabled}
              />
              <CategoryRow
                label={m.cookie_consent_payment()}
                description={m.cookie_consent_payment_desc()}
                checked={true}
                disabled={true}
              />
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setView("initial")}
                className="btn btn-ghost btn-sm"
              >
                {m.common_back()}
              </button>
              <button
                type="button"
                onClick={handleSavePreferences}
                className="btn btn-primary btn-sm"
              >
                {m.cookie_consent_save()}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* Cookie icon — inline SVG, lucide stroke weight */
function CookieIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
      <path d="M8.5 8.5v.01M16 15.5v.01M12 12v.01M11 17v.01M7 14v.01" />
    </svg>
  );
}

/**
 * Single consent category row with toggle switch.
 * Renders as a flat row (borders + bg provided by the parent container).
 */
function CategoryRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
}) {
  const id = React.useId();

  return (
    <div
      className="flex items-center gap-4 px-4 py-3"
      style={{ borderColor: "var(--border-subtle)" }}
    >
      <div className="flex-1 min-w-0">
        <label
          htmlFor={id}
          className="text-sm font-medium"
          style={{ color: disabled ? "var(--text-muted)" : "var(--text)" }}
        >
          {label}
        </label>
        <p
          className="text-xs mt-0.5"
          style={{ color: "var(--text-muted)" }}
        >
          {description}
        </p>
      </div>
      <button
        id={id}
        role="switch"
        type="button"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        className="shrink-0 relative inline-flex h-5 w-9 items-center rounded-full"
        style={{
          background: checked ? "var(--accent)" : "var(--border-strong)",
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "background-color var(--duration-fast, 120ms) var(--ease-out)",
        }}
        aria-label={`${label} — ${checked ? "enabled" : "disabled"}`}
      >
        <span
          className="inline-block h-3.5 w-3.5 rounded-full"
          style={{
            background: "#fff",
            transform: checked ? "translateX(19px)" : "translateX(3px)",
            transition: "transform var(--duration-fast, 120ms) var(--ease-out)",
            boxShadow: "0 1px 2px rgba(0,0,0,0.15)",
          }}
        />
      </button>
    </div>
  );
}
